import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import 'mobile-drag-drop-shadow-dom';

type DragState = {
    draggedElement?: string;
    hoveredElement?: string;
    isDropPlaceholderLast?: boolean;
    hoveredList?: 'rows' | 'columns';
}

@customElement('maaling-pivot-element')
export class MaalingPivotElement extends LitElement {
    static styles = css`
        @media only screen and (min-width: 992px) {
            .container {
                width: 560px;
            }
        }
        .drag-list-container {
            display: flex;
            justify-content: flex-start;
        }
        .pivot-button {
            background-color: var(--main-blue);
            color: white;
            border: none;
            padding: 10px;
            font-weight: bold;
            cursor: pointer;
            width: 100%;
            display: block;
            margin-left: auto;
            margin-right: auto;
            margin-top: 20px;
            position: relative;
            z-index: 99;
        }
        .rows-container, .columns-container {
            width: 230px;
            margin: 0;
        }
        .rows-container {
            padding-right: 20px;
            border-right: solid 2px var(--main-gray-25);
        }
        .columns-container {
            padding-left: 20px;
        }
        ul {
            min-heigth: 120px;
            padding: 20px 0;
            margin: 5px 0 0 0;
            width: 100%;
            position: relative;
            height: 100%;
        }
        li {
            display: block;
            border: 1px solid #b5b5b5;
            background: var(--main-blue-10);
            color:var(--main-black);
            padding: 15px 10px;
            cursor: grab;
            min-height: 22px;
        }
        li:active {
            cursor: grabbing;
        }
        li[draggable="false"], li[draggable="false"]:active {
            cursor: not-allowed;
        }
        .title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
        }
        .li-container {
            display: flex;
            gap: 10px;
            align-items: center;
            justify-content: space-between;
        }
        .li-container * {
            min-width: 20px;
        }
        .divider {
            border: 1px solid var(--main-gray-10);
            width: 90%;
            margin: 10px auto;
        }
        #DropPlaceholder {
            background: none;
            border-style: dashed;
        }
        figcaption, div {
            user-select: none;
        }
        .description {
            margin-bottom: 15px;
        }
    `;

    @property({ type: Array })
    rows: string[] = [];

    @property({ type: Array })
    columns: string[] = [];

    @property({
        type : Map,
    })
    relatedDimensions: Map<string, string[]> = new Map();

    @state()
    dragRows?: string[];

    @state()
    dragColumns?: string[];

    @state()
    dragState: DragState = {};

    @state()
    activeDragElement?: HTMLLIElement;

    willUpdate(changedProperties: Map<string, any>) {
        if (changedProperties.has('columns') || changedProperties.has('rows')) {
            this.relatedDimensions.forEach((value, key) => {
                if (this.rows.includes(key)) {
                    this.rows = this.rows.filter(a => !value.includes(a));
                }
                else if (this.columns.includes(key)) {
                    this.columns = this.columns.filter(a => !value.includes(a));
                }
            });

            this.dragRows = this.rows;
            this.dragColumns = this.columns;
        }
    }

    renderDropPlaceholder(listType: 'top' | 'bottom') {
        if (listType === 'top') {
            return html`
            <li id="DropPlaceholder" draggable="true" @dragover=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} @dragenter=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} > </li>
            <div class="divider"></div>
        `;
        } else {
            return html`
             <div class="divider"></div>
            <li id="DropPlaceholder" draggable="true" @dragover=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} @dragenter=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} > </li>
        `;
        }
     
    }

     render() {
         return html`
            <section class="container">
                <div class="title">Pivotering</div>
                <div class="description">Du flytter rundt på rækker og kolonner ved at trække variable fra kolonne til række eller omvendt, alt efter hvilken visning du ønsker. Der skal dog altid være mindst én variabel i henholdsvis kolonne og række. Ligeledes kan du bestemme rækkefølgen på variablene ved at trække variablene op eller ned i rækkefølgen.</div>
                <div class="drag-list-container">
                    <figure class="rows-container">
                        <figcaption><strong>Rækker</strong></figcaption>
                        <ul @drop="${() => this.droppedElement('rows')}" @dragover=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} @dragenter=${(ev: DragEvent) => this.hoverList(ev, 'rows')}>
                            ${this.dragRows?.map((row, idx, arr) => html`
                                ${this.dragState.hoveredElement === row && !this.dragState.isDropPlaceholderLast && this.dragState.hoveredElement !== this.dragState.draggedElement ? this.renderDropPlaceholder('top') : ''}
                                <li draggable=${this.dragRows && this.dragRows?.length > 1 ? 'true' : 'false'} @dragover=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} @dragenter=${(ev: DragEvent) => this.hoverElement(row, ev)} @dragstart="${(ev: DragEvent) => this.dragStart(row, ev)}">
                                    <div class="li-container">
                                        <strong>${row}</strong>
                                        ${this.dragRows && this.dragRows?.length > 1 ? 
                                        html`<svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.25 8.2513H11.9167V5.5013H14.6667L10.0833 0.917969L5.5 5.5013H8.25V8.2513ZM7.33333 9.16797H4.58333V6.41797L0 11.0013L4.58333 15.5846V12.8346H7.33333V9.16797ZM20.1667 11.0013L15.5833 6.41797V9.16797H12.8333V12.8346H15.5833V15.5846L20.1667 11.0013ZM11.9167 13.7513H8.25V16.5013H5.5L10.0833 21.0846L14.6667 16.5013H11.9167V13.7513Z" fill="#99938B"/>
                                            </svg>` : ''}
                                    </div>
                                </li>
                                ${idx + 1 !== arr.length ? html`<div class="divider"></div>` : '' }
                            `)}
                            ${this.dragState.isDropPlaceholderLast && this.dragState.hoveredList === 'rows' && this.dragRows && this.dragRows[this.dragRows.length - 1] !== this.dragState.draggedElement ? this.renderDropPlaceholder('bottom') : ''}
                        </ul>
                    </figure>
                    <figure class="columns-container">
                        <figcaption><strong>Kolonner</strong></figcaption>
                        <ul @drop=${() => this.droppedElement('columns')} @dragover=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} @dragenter=${(ev: DragEvent) => this.hoverList(ev, 'columns')}>
                            ${this.dragColumns?.map((column, idx, arr) => html`
                                ${this.dragState.hoveredElement === column && !this.dragState.isDropPlaceholderLast && this.dragState.hoveredElement !== this.dragState.draggedElement  ? this.renderDropPlaceholder('top') : ''}
                                <li draggable=${this.dragColumns && this.dragColumns.length > 1 ? 'true' : 'false'} @dragover=${(ev: DragEvent) => { ev.preventDefault(); ev.stopPropagation(); }} @dragenter=${(ev: DragEvent) => this.hoverElement(column, ev)} @dragstart="${(ev: DragEvent) => this.dragStart(column, ev)}">
                                    <div class="li-container">
                                        <strong>${column}</strong>
                                        ${this.dragColumns && this.dragColumns?.length > 1 ? 
                                        html`<svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.25 8.2513H11.9167V5.5013H14.6667L10.0833 0.917969L5.5 5.5013H8.25V8.2513ZM7.33333 9.16797H4.58333V6.41797L0 11.0013L4.58333 15.5846V12.8346H7.33333V9.16797ZM20.1667 11.0013L15.5833 6.41797V9.16797H12.8333V12.8346H15.5833V15.5846L20.1667 11.0013ZM11.9167 13.7513H8.25V16.5013H5.5L10.0833 21.0846L14.6667 16.5013H11.9167V13.7513Z" fill="#99938B"/>
                                            </svg>` : ''}
                                    </div>
                                </li>
                                ${idx + 1 !== arr.length ? html`<div class="divider"></div>` : '' }
                            `)}
                            ${this.dragState.isDropPlaceholderLast && this.dragState.hoveredList === 'columns' && this.dragColumns && this.dragColumns[this.dragColumns.length - 1] !== this.dragState.draggedElement ? this.renderDropPlaceholder('bottom') : ''}
                        </ul>
                    </figure>
                </div>
                <button class="pivot-button" @click=${() => this.pivot()}>Anvend</button>
            </section>
        `;
     }

    hoverList(ev: DragEvent, listType: 'rows' | 'columns') {
        ev.preventDefault();
        ev.stopPropagation();
        const listTarget = ev.currentTarget as HTMLElement;
        const offset = ev.offsetY;
        let visitedDropPlaceholder = false;
        let hoveredElement: any;
        const hoveredList = listType;
        let isDropPlaceholderLast = false;
        for (let i = 0; i < listTarget.children.length; i++) {
            const child = listTarget.children.item(i);
            if (child?.id === 'DropPlaceholder') {
                visitedDropPlaceholder = true;
            }
            if ((child as HTMLElement).offsetTop > offset && child?.id !== 'DropPlaceholder'  && this.dragRows && this.dragColumns) {
                if (visitedDropPlaceholder) {
                    hoveredElement = listType === 'rows' ? this.dragRows[i - 1] : this.dragColumns[i - 1];
                } else {
                    hoveredElement = listType === 'rows' ? this.dragRows[i] : this.dragColumns[i];
                }
                break;
            }
            if (i === listTarget.children.length - 1) {
                // Due to break condition in the previous, we know that it has to be last at this point
                isDropPlaceholderLast = true;
            }
        }
        this.dragState = {
            ...this.dragState,
            isDropPlaceholderLast,
            hoveredElement,
            hoveredList,
        };

    }

    hoverElement(key: string, ev: DragEvent) {
        ev.preventDefault();
        ev.stopPropagation();
        this.dragState = {
            ...this.dragState,
            hoveredElement: key,
        };
    }

    dragStart(key: string, ev: DragEvent) {
        this.dragState = {
            ...this.dragState,
            draggedElement: key,
        };
        if (ev.dataTransfer) {
            ev.dataTransfer.dropEffect = 'move';
        }

        this.startHighlight(ev);
    }

    startHighlight(ev: DragEvent) {
        if (this.activeDragElement) {
            this.activeDragElement.style.backgroundColor = 'var(--main-blue-10)';
        }

        this.activeDragElement = ev.target as HTMLLIElement;
        this.activeDragElement.style.backgroundColor = 'var(--main-blue-25)';
    }

    endHighlight() {
        if (this.activeDragElement) {
            this.activeDragElement.style.backgroundColor = 'var(--main-blue-10)';
        }
    }

    droppedElement(listType: 'rows' | 'columns') {
        this.endHighlight();

        if (this.dragState.draggedElement === this.dragState.hoveredElement) {
            this.dragState = {};
            return;
        } else if (this.dragState.draggedElement) {
            const newColumns = this.dragColumns?.filter(col => col !== this.dragState.draggedElement);
            const newRows = this.dragRows?.filter(row => row !== this.dragState.draggedElement);
            if (this.dragState.hoveredElement) {
                let targetIdx = -1;
                if (listType === 'columns') {
                    targetIdx = newColumns?.indexOf(this.dragState.hoveredElement) ?? -1;
                } else if (listType === 'rows') {
                    targetIdx = newRows?.indexOf(this.dragState.hoveredElement) ?? -1;
                }
                if (targetIdx > -1) {
                    if (listType === 'columns') {
                        newColumns?.splice(targetIdx, 0, this.dragState.draggedElement);
                    } else if (listType === 'rows') {
                        newRows?.splice(targetIdx, 0, this.dragState.draggedElement);
                    }
                    
                }
            } else {
                if (listType === 'columns') {
                    newColumns?.push(this.dragState.draggedElement);
                } else if (listType === 'rows') {
                    newRows?.push(this.dragState.draggedElement);
                }
            }

            this.dragRows = newRows;
            this.dragColumns = newColumns;
            this.dragState = {};
        }
    }

    pivot() {
        const transformedCol = this.dragColumns?.flatMap<string>(col => this.relatedDimensions.get(col) ? [col, ...(this.relatedDimensions.get(col) as string[])] : col);
        const transformedRow = this.dragRows?.flatMap<string>(row => this.relatedDimensions.get(row) ?  [row, ...(this.relatedDimensions.get(row) as string[])] : row);
        
        const pivotedEvent = new CustomEvent('pivoted', {
            detail: {
                columns: transformedCol,
                rows: transformedRow,
            },
            composed: true,
            bubbles: true,
        });

        //// Persist the transformed pivot changes in session storage & dispatch pivot event

        //let path = window.location.pathname;

        //while (path.startsWith('/')) {
        //    path = path.slice(1);
        //}

        //while (path.length > 1 && path.endsWith('/')) {
        //    path = path.slice(0, -1);
        //}

        //const colKey = path + "_transformedCol";
        //const rowKey = path + "_transformedRow";

        //localStorage.setItem(colKey, JSON.stringify(transformedCol))
        //localStorage.setItem(rowKey, JSON.stringify(transformedRow))

        this.dispatchEvent(pivotedEvent);
    }
}